import { DOCUMENT } from '@angular/common';
import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  model,
} from '@angular/core';

export interface TimeListOption {
  value: string;
  label: string;
  disabled: boolean;
}

let nextTimeListId = 0;

@Component({
  selector: 'cue-time-list',
  templateUrl: './time-list.component.html',
  styleUrl: './time-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'listbox',
    tabindex: '0',
    '[attr.aria-label]': 'label()',
    '[attr.aria-activedescendant]': 'optionId(value())',
    '(pointerenter)': 'focusOnHover($event)',
    '(pointerdown)': 'interruptScroll()',
    '(scroll)': 'handleScroll()',
    '(wheel)': 'handleWheel($event)',
    '(keydown)': 'handleKeydown($event)',
  },
})
export class TimeListComponent {
  readonly options = input.required<TimeListOption[]>();
  readonly value = model.required<string>();
  readonly label = input.required<string>();
  readonly enableMouseWheel = input(true);

  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly document = inject<Document>(DOCUMENT);
  private readonly id = `cue-time-list-${nextTimeListId++}`;
  private previousValue: string | undefined;
  private programmaticScroll = false;
  private scrollTimer?: ReturnType<typeof setTimeout>;
  private animationFrame?: number;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.interruptScroll());
    afterRenderEffect(() => {
      const value = this.value();
      this.options();
      this.centerOption(value, this.previousValue !== undefined);
      this.previousValue = value;
    });
  }

  optionId(value: string): string {
    return `${this.id}-${value}`;
  }

  selectOption(option: TimeListOption): void {
    if (option.disabled) {
      return;
    }
    this.element.focus({ preventScroll: true });
    if (option.value !== this.value()) {
      this.value.set(option.value);
    } else {
      this.centerOption(option.value, true);
    }
  }

  focusOnHover(event: PointerEvent): void {
    if (event.pointerType === 'mouse') {
      this.element.focus({ preventScroll: true });
    }
  }

  interruptScroll(): void {
    clearTimeout(this.scrollTimer);
    if (this.animationFrame !== undefined) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = undefined;
    }
    this.programmaticScroll = false;
  }

  handleScroll(): void {
    clearTimeout(this.scrollTimer);
    // Also handles browsers without scrollend, including older mobile Safari.
    this.scrollTimer = setTimeout(() => {
      if (!this.element.isConnected) {
        return;
      }
      if (this.programmaticScroll) {
        this.programmaticScroll = false;
        return;
      }
      const center = this.element.scrollTop + this.element.clientHeight / 2;
      const options = Array.from(this.element.querySelectorAll<HTMLButtonElement>('[role="option"]:not(:disabled)'));
      const nearest = options.reduce<HTMLButtonElement | undefined>((previous, option) => {
        const distance = (item: HTMLElement) => Math.abs(item.offsetTop + item.offsetHeight / 2 - center);
        return !previous || distance(option) < distance(previous) ? option : previous;
      }, undefined);
      const option = this.options().find((option) => option.value === nearest?.value);
      if (option) {
        this.selectOption(option);
      }
    }, 120);
  }

  handleWheel(event: WheelEvent): void {
    event.preventDefault();
    if (this.enableMouseWheel() && event.deltaY) {
      this.moveSelection(event.deltaY > 0 ? 1 : -1);
    }
  }

  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.moveSelection(event.key === 'ArrowDown' ? 1 : -1);
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const enabled = this.options().filter((option) => !option.disabled);
      const option = event.key === 'Home' ? enabled[0] : enabled[enabled.length - 1];
      if (option) {
        this.selectOption(option);
      }
    }
  }

  private moveSelection(direction: number): void {
    const options = this.options();
    const current = options.findIndex((option) => option.value === this.value());
    for (let index = current + direction; index >= 0 && index < options.length; index += direction) {
      if (!options[index].disabled) {
        this.selectOption(options[index]);
        return;
      }
    }
  }

  private centerOption(value: string, animate: boolean): void {
    const selected = this.document.getElementById(this.optionId(value));
    if (!selected || !this.element.clientHeight) {
      return;
    }
    this.interruptScroll();
    const top = selected.offsetTop - (this.element.clientHeight - selected.offsetHeight) / 2;
    if (Math.abs(this.element.scrollTop - top) < 1) {
      return;
    }
    const reducedMotion = this.document.defaultView?.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.programmaticScroll = true;
    if (!animate || reducedMotion) {
      this.element.scrollTop = top;
      return;
    }
    const from = this.element.scrollTop;
    const started = performance.now();
    // Native smooth scrolling may be disabled by the browser even without reduced motion.
    const frame = (now: number) => {
      const progress = Math.min((now - started) / 180, 1);
      this.element.scrollTop = from + (top - from) * (1 - Math.pow(1 - progress, 3));
      this.animationFrame = progress < 1 ? requestAnimationFrame(frame) : undefined;
    };
    this.animationFrame = requestAnimationFrame(frame);
  }
}
